/**
 * The password policy of owner and manager logins (Supabase Auth). Auth enforces it on every
 * password change: `supabase/config.toml` sets `minimum_password_length = 10` and
 * `password_requirements = "lower_upper_letters_digits_symbols"`, and the hosted project uses the
 * same settings (docs/runbooks/supabase-setup.md). This mirror only explains the rules before a new
 * password is sent; a password it accepts is still checked by Auth.
 *
 * Auth counts bytes (UTF-8), not characters, and refuses more than 72 (the bcrypt limit). Each
 * required character must come from Auth's own sets: a space, an accented letter or a symbol such as
 * € counts towards the length only.
 */
export const PASSWORD_MIN_BYTES = 10;
export const PASSWORD_MAX_BYTES = 72;

export const PASSWORD_LOWER = 'abcdefghijklmnopqrstuvwxyz';
export const PASSWORD_UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const PASSWORD_DIGITS = '0123456789';
export const PASSWORD_SYMBOLS = '!@#$%^&*()_+-=[]{};\'\\:"|<>?,./`~';

export type PasswordRule = 'length' | 'lower' | 'upper' | 'digit' | 'symbol' | 'max';

/** The rules in the order a form lists them. `max` is shown only when it is broken. */
export const PASSWORD_RULES: readonly { rule: PasswordRule; label: string }[] = [
  {
    rule: 'length',
    label: `At least ${PASSWORD_MIN_BYTES} characters (an accented letter such as é counts as two)`,
  },
  { rule: 'lower', label: 'A lower-case letter (a–z)' },
  { rule: 'upper', label: 'An upper-case letter (A–Z)' },
  { rule: 'digit', label: 'A digit (0–9)' },
  { rule: 'symbol', label: 'A symbol, such as ! @ # $ % - _ ?' },
  {
    rule: 'max',
    label: `No more than ${PASSWORD_MAX_BYTES} characters (an accented letter counts as two)`,
  },
];

const has = (password: string, set: string) => [...password].some((c) => set.includes(c));

/** The rules `password` breaks, in the order of PASSWORD_RULES; empty when Auth would accept it. */
export function passwordProblems(password: string): PasswordRule[] {
  const bytes = new TextEncoder().encode(password).length;
  const broken: PasswordRule[] = [];
  if (bytes < PASSWORD_MIN_BYTES) broken.push('length');
  if (!has(password, PASSWORD_LOWER)) broken.push('lower');
  if (!has(password, PASSWORD_UPPER)) broken.push('upper');
  if (!has(password, PASSWORD_DIGITS)) broken.push('digit');
  if (!has(password, PASSWORD_SYMBOLS)) broken.push('symbol');
  if (bytes > PASSWORD_MAX_BYTES) broken.push('max');
  return broken;
}

export interface NewPasswordCheck {
  problems: PasswordRule[];
  /** Both fields hold the same text (and the second is not empty). */
  matches: boolean;
  ok: boolean;
}

/**
 * A new password typed twice. Nothing is trimmed: Auth keeps spaces, so a password with a leading or
 * trailing space is a different password.
 */
export function checkNewPassword(password: string, confirm: string): NewPasswordCheck {
  const problems = passwordProblems(password);
  const matches = confirm.length > 0 && password === confirm;
  return { problems, matches, ok: problems.length === 0 && matches };
}
